// ThemeContext — manages light/dark/system theme preference with localStorage persistence.
// Adds/removes 'dark' class on <html> element for Tailwind dark mode.
// resolvedTheme is derived during render; systemPreference is updated by a
// matchMedia subscription callback (the allowed setState-in-subscription pattern).

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = 'contractgen-theme';

function safeGetItem(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function safeSetItem(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemPreference(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(resolved: ResolvedTheme) {
  if (resolved === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    return (safeGetItem(STORAGE_KEY) as Theme) || 'system';
  });

  // systemPreference is only updated by the matchMedia subscription callback —
  // setState in a subscription callback is the allowed pattern.
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(
    () => getSystemPreference(),
  );

  // resolvedTheme is derived during render — no separate state needed.
  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemPreference : theme;

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    safeSetItem(STORAGE_KEY, newTheme);
    const resolved: ResolvedTheme =
      newTheme === 'system' ? getSystemPreference() : newTheme;
    applyTheme(resolved);
  }, []);

  // Apply the resolved theme to the DOM and subscribe to system preference
  // changes. setSystemPreference here is inside a subscription callback —
  // the allowed setState-in-subscription pattern per react-hooks/set-state-in-effect.
  useEffect(() => {
    applyTheme(resolvedTheme);

    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (!mediaQuery || typeof mediaQuery.addEventListener !== 'function') return;
    const handleChange = () => {
      const newSys = getSystemPreference();
      setSystemPreference(newSys);
      if (theme === 'system') applyTheme(newSys);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, resolvedTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
